// Every settings section writes to the same `settings` model, so the record id
// is what says which settings changed. Ids are the section files in the API
// (schema-api-server/api/com/settings); labels follow the admin's own names
// where the section has a page, and describe the rest plainly.
export const EVENT_SETTINGS_LABELS = Object.freeze({
  accounts: 'Customer',
  admin: 'Admin',
  checkout: 'Checkout',
  discounts: 'Discount',
  general: 'General',
  geo: 'Geolocation',
  giftcards: 'Gift card',
  integrations: 'Integration',
  notifications: 'Notification',
  orders: 'Order',
  payments: 'Payment',
  products: 'Product',
  purchaselinks: 'Purchase link',
  shipments: 'Shipping',
  store: 'Store',
  subscriptions: 'Subscription',
  taxes: 'Tax',
});

// Settings sections that have a page in the admin, mapped from the settings
// record id used by `setting.updated` events. Sections not listed here (admin,
// geo, purchaselinks, index) render without a link, because Settings falls back
// to the index page instead of a 404 for unknown sections.
export const EVENT_SETTINGS_SECTIONS = Object.freeze({
  accounts: 'customers',
  checkout: 'checkout',
  customers: 'customers',
  discounts: 'discounts',
  general: 'general',
  giftcards: 'giftcards',
  integrations: 'integrations',
  notifications: 'notifications',
  orders: 'orders',
  payments: 'payments',
  products: 'products',
  shipments: 'shipping',
  shipping: 'shipping',
  store: 'general',
  subscriptions: 'subscriptions',
  taxes: 'taxes',
  webhooks: 'webhooks',
});

// Event icons, by the last segment of event.type
export const EVENT_ICONS = Object.freeze({
  created: 'events/order-created',
  deleted: 'events/items-canceled',
  canceled: 'events/order-canceled',
  submitted: 'events/order-submitted',
  paid: 'events/payment',
  refunded: 'events/payment',
  payment_failed: 'events/payment',
});

export const EVENT_ICON_DEFAULT = 'events/items-changed';

// Keys whose values are sensitive. Mirrors the API's masking regex
// (schema-api-server server/util.js SETTING_IS_SENSITIVE_REGEX), which only
// masks `settings` events — values on other models are stored raw.
export const EVENT_SENSITIVE_KEY_REGEX =
  /(^|[._])(key|key_id|live_password|passphrase|pem|pin|secret|signature|test_password|token|password)$/i;

// Field values shown on a row, nested included, before the rest move behind
// "more fields"
export const EVENT_VISIBLE_FIELD_LIMIT = 10;

export const EVENT_PAGE_LIMIT = 50;

// Product cap on a single export.
export const EVENT_EXPORT_LIMIT = 10000;

// The settings model records one type for every section, so its filter works
// off the section id instead
export const EVENT_SETTINGS_MODEL = 'settings';

// Sentinel value for the "API / system" option in the user filter
export const EVENT_USER_NONE = 'none';

// Date range filter, matching the shape of LOG_LEVEL_FILTERS in constants/logs.
// Dates arrive as moment objects from DateRangePicker and as ISO strings when
// restored from the URL, so they are normalized to ISO here rather than relying
// on JSON serialization.
export const EVENT_FILTERS = Object.freeze({
  dates: (values) => {
    const { startDate, endDate } = values.dates || {};

    if (!startDate && !endDate) {
      return {};
    }

    return {
      date_created: {
        ...(startDate && { $gte: new Date(startDate).toISOString() }),
        ...(endDate && { $lte: new Date(endDate).toISOString() }),
      },
    };
  },
  model: (values) => (values.model ? { model: values.model } : {}),
  type: (values) => {
    if (!values.type) {
      return {};
    }

    // For settings the picker offers sections rather than event types, and a
    // section is recorded as the event's record id
    return values.model === EVENT_SETTINGS_MODEL
      ? { 'data.id': values.type }
      : { type: values.type };
  },
  user: (values) => {
    if (!values.user) {
      return {};
    }

    // Events written outside the dashboard (API keys, storefront, apps,
    // background tasks) carry no user_id at all. `null` matches both missing
    // and explicitly null values, and is an index point-lookup.
    return values.user === EVENT_USER_NONE
      ? { user_id: null }
      : { user_id: values.user };
  },
});
